import type { SpecType } from '@local/lib';

import { ProductPriceBasisType } from '@local/lib';
import { Combobox } from '@salesforce/design-system-react';

import type { ProductPriceBasisState } from '../qualityFormulaTypes';

import {
  CONTRACT_PRICE,
  CONTRACT_PRICE_COMPONENTS,
  CONTRACT_UNIT_OF_MEASURE,
  FIELD_HELP,
  PRODUCT_PRICE_BASIS_OPTIONS,
} from '../qualityFormulaMetadata';
import { FieldLabelRow } from './FieldLabelRow';
import { FormulaField } from './FormulaField';
import { PriceComponentsTable } from './PriceComponentsTable';
import { formatCurrency } from './utils/resultFormatting';

/** Shared shape for Price Basis' two inline sub-fields (`ProductPriceBasisField`/`DeemedPriceField`) — see `SpecCardPriceBasisFieldSlot` in QualitySpecCard.tsx, which renders either against this same interface. */
export interface ProductPriceBasisSubFieldProps {
  /** Locks the field to its current value — used by the Template Editor, where Price Basis always
   * stays Contract Price; changing it to Deemed Price or Price Components is a Contract Entry
   * action only. */
  disabled?: boolean;
  onChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  priceBasis: ProductPriceBasisState;
  specType: SpecType;
}

/**
 * The "Price Basis" column rendered inline alongside Formula Type / Tier Mode / Decimal Precision
 * — a read-only `Combobox` (SLDS' picklist-equivalent) so all four read as one uniform, compact
 * row instead of this field alone standing out or growing the card taller. Only ever rendered by
 * QualitySpecCard while `formulaType` is PERCENTAGE ("% of Price") — see business rule in
 * SpecCardPriceBasisSlot.
 */
export const ProductPriceBasisField: React.FC<ProductPriceBasisSubFieldProps> = ({
  disabled,
  onChange,
  priceBasis,
  specType,
}) => {
  const selectedOption = PRODUCT_PRICE_BASIS_OPTIONS.find(option => option.value === priceBasis.type);

  return (
    <div>
      <FieldLabelRow label="Price Basis" tooltip={FIELD_HELP.priceBasisType} />
      <Combobox
        assistiveText={{ label: 'Price Basis' }}
        disabled={disabled}
        events={{
          onSelect: (_event, data) => {
            const selectedId = data.selection[0]?.id;
            if (!selectedId) return;
            // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
            const nextType = selectedId as ProductPriceBasisType;
            const update: Partial<ProductPriceBasisState> = { type: nextType };
            // Price Components starts pre-populated with every catalog component — matching the
            // Sulfur reference's own already-populated table, instead of an empty one the user
            // has to build up one "+ Add Component" click at a time — but only the *first* time
            // (nothing selected yet); switching back to this basis later never clobbers whatever
            // the user has since added or removed.
            if (nextType === ProductPriceBasisType.PriceComponents && priceBasis.selectedComponentKeys.length === 0) {
              update.selectedComponentKeys = CONTRACT_PRICE_COMPONENTS.map(component => component.key);
            }
            onChange(specType, update);
          },
        }}
        id={`price-basis-${specType}`}
        labels={{ placeholder: 'Select Price Basis' }}
        options={PRODUCT_PRICE_BASIS_OPTIONS.map(option => ({ id: option.value, label: option.label }))}
        predefinedOptionsOnly
        selection={selectedOption ? [{ id: selectedOption.value, label: selectedOption.label }] : []}
        variant="readonly"
      />
    </div>
  );
};

/**
 * The Deemed Price amount, rendered as its own column immediately next to Price Basis (not below
 * the row) since it's the one Price Basis option with no other follow-up content — there's no
 * reason to grow the card taller for a single number. Only mounted while `priceBasis.type` is
 * DeemedPrice; see `SpecCardPriceBasisFieldSlot` in QualitySpecCard.tsx.
 */
export const DeemedPriceField: React.FC<ProductPriceBasisSubFieldProps> = ({ onChange, priceBasis, specType }) => (
  <FormulaField
    fallback={0}
    label="Deemed Price ($)"
    onChange={value => {
      onChange(specType, { deemedPrice: value });
    }}
    tooltip={FIELD_HELP.priceBasisDeemedPrice}
    value={priceBasis.deemedPrice}
  />
);

/**
 * The read-only "Full contract price" callout shown next to Price Basis whenever it's Contract
 * Price — the one Price Basis option with no field of its own to configure (Deemed Price gets
 * `DeemedPriceField`, Price Components gets the full table), so without this the row would just
 * read as a bare dropdown with no indication of what value the formula actually scales against.
 * Matches the Ash reference's own "Full contract price $112.00 / MT · No components or override"
 * note — both lines read from this feature's own shared example-contract constants (the same ones
 * `ContractPricingSummarySection`'s summary line and the Playground's starting Contract Price use)
 * rather than any new/invented figure.
 */
export const ContractPriceBasisInfo: React.FC = () => (
  <div
    className="slds-p-vertical_xx-small slds-p-horizontal_small"
    style={{
      alignItems: 'center',
      backgroundColor: 'var(--slds-g-color-accent-light-1)',
      borderRadius: 'var(--slds-g-radius-border-1)',
      columnGap: 'var(--slds-g-spacing-2)',
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: '2rem',
    }}
  >
    <p className="slds-text-body_small" style={{ margin: 0 }}>
      Full Contract Price
    </p>
    <p
      className="slds-text-body_small"
      style={{ color: 'var(--slds-g-color-accent-1)', fontWeight: 'var(--slds-g-font-weight-7)', margin: 0 }}
    >
      {formatCurrency(CONTRACT_PRICE)} / {CONTRACT_UNIT_OF_MEASURE} · No Components or Override
    </p>
  </div>
);

interface ProductPriceBasisDetailsProps {
  /** Lets the user collapse Price Components (see SpecSectionPanel's own doc comment) — only for
   * Range, where it stacks full-width alongside Price Adjustment Ranges; Threshold's split layout
   * already gives it its own column, so collapsing it there would serve no purpose. */
  collapsible?: boolean;
  /** Stretches Price Components to fill its sibling column's height (see SpecSectionPanel's own
   * doc comment) — only meaningful alongside that column, so callers outside QualitySpecCard's
   * split layout never pass this. */
  fillHeight?: boolean;
  onChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  priceBasis: ProductPriceBasisState;
  specType: SpecType;
}

/**
 * The follow-up content for Price Components — the only Price Basis option with content too
 * large to fit inline next to the Combobox (Contract Price needs nothing, Deemed Price is a
 * single inline field, see `DeemedPriceField`) — rendered full-width below the Formula Type row,
 * only while that basis is actually selected.
 */
export const ProductPriceBasisDetails: React.FC<ProductPriceBasisDetailsProps> = ({
  collapsible,
  fillHeight,
  onChange,
  priceBasis,
  specType,
}) => {
  const addComponent = (key: string) => {
    if (priceBasis.selectedComponentKeys.includes(key)) return;
    onChange(specType, { selectedComponentKeys: [...priceBasis.selectedComponentKeys, key] });
  };

  const removeComponent = (key: string) => {
    onChange(specType, {
      selectedComponentKeys: priceBasis.selectedComponentKeys.filter(existing => existing !== key),
    });
  };

  if (priceBasis.type !== ProductPriceBasisType.PriceComponents) return null;

  return (
    <PriceComponentsTable
      collapsible={collapsible}
      fillHeight={fillHeight}
      onAdd={addComponent}
      onRemove={removeComponent}
      selectedKeys={priceBasis.selectedComponentKeys}
    />
  );
};
